import {
    RequestNoRestrictionSummary,
    RequestPreviewResponseDto,
    RequestWithRestrictionSummary,
} from '@/domain/newRequest';
import { DEFAULT_NUMBER_OF_DAYS } from '@/utils/constants/common';

import { handleRangesInfo } from './utils';

const setZeroIfLessThanZero = (count: number) => (count <= DEFAULT_NUMBER_OF_DAYS ? DEFAULT_NUMBER_OF_DAYS : count);

export const useRequestsConverter = () => {
    const convertRequestPreviewResponseDtoToRequestSummary = (
        dto: RequestPreviewResponseDto
    ): RequestNoRestrictionSummary | RequestWithRestrictionSummary => {
        const { absenceLimitType, ranges, restriction, isOverLimit, absenceTypeDetail, daysAvailable } = dto;
        const { noLimits, limit, periodType: currentPeriodType, limitPeriodType, allowOverbooking } = absenceLimitType;
        const { daysLeft: rangesDaysLeft, daysUsedByOtherTypesWithinLimit } = ranges?.[0] ?? {};

        const { daysUsed, daysUsedAdditional } = ranges.reduce(
            (acc, range) => {
                acc.daysUsed += range.daysUsed;
                acc.daysUsedAdditional += range.daysUsedAdditional;
                return acc;
            },
            { daysUsed: 0, daysUsedAdditional: 0 }
        );
        const { hasRestriction, restriction: restrictionLimit, restrictionPeriodType } = restriction ?? {};

        const periodType = restrictionPeriodType ?? currentPeriodType ?? limitPeriodType ?? undefined;
        const leftDays = noLimits ? DEFAULT_NUMBER_OF_DAYS : rangesDaysLeft ?? DEFAULT_NUMBER_OF_DAYS;
        const parentWithRestriction = hasRestriction ? currentPeriodType ?? limitPeriodType ?? undefined : undefined;
        const daysUsedChild = daysUsed;
        const daysUsedParent = (daysUsedByOtherTypesWithinLimit ?? DEFAULT_NUMBER_OF_DAYS) + daysUsedChild;
        const childLeftDays = (restrictionLimit ?? 0) - daysUsedChild - daysUsedAdditional;
        const calculatedChildLefDays = childLeftDays > leftDays && !noLimits ? leftDays : childLeftDays;

        const commonRequestResult = {
            periodType,
            daysUsedAdditional,
            isOverLimit,
            allowOverbooking,
            daysAvailable,
            includeNextRange: handleRangesInfo(periodType, ranges),
        };

        const noRestrictionResult = {
            ...commonRequestResult,
            noLimits,
            limit,
            // A parent's pool is also drained by the days taken on its child types
            daysUsed: daysUsedParent,
            leftDays,
        };

        const withRestrictionResult = {
            ...commonRequestResult,
            noLimits: false,
            parentWithRestriction,
            limit: restrictionLimit,
            /*
             * The pool line reports what the person may actually take this period: the configured limit
             * knows nothing of the carried over days, nor of the corrections - its own or its children's.
             */
            limitParent: noLimits ? undefined : daysAvailable,
            daysUsedChild,
            daysUsedParent,
            parentName: absenceLimitType?.name,
            childName: absenceTypeDetail?.name,
            childLeftDays: setZeroIfLessThanZero(calculatedChildLefDays),
        };

        return parentWithRestriction ? withRestrictionResult : noRestrictionResult;
    };

    return {
        convertRequestPreviewResponseDtoToRequestSummary,
    };
};
